import { PayloadAction } from "@reduxjs/toolkit";
import { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { selectActivationCode } from "scenes/selectors";
import factory from "utils/getApiHandlerEffect";
import { ErrorMessageEnum } from "./enums";

import storage from "../../persistance";

import {
  afterChallengeQuestionFetch,
  afterChallengeQuestionSubmissionResponse,
  loadChallengeQuestion,
  onAnswerSubmission,
  setEmailError,
  setEmailExpired,
  setError,
  setInvalidCode,
  setRetries,
} from "./redux";
import {
  ChallengeQuestionResponseType,
  ChallengeQuestionSubmissionPayloadType,
  ChallengeQuestionSubmissionResponsePayload,
} from "./types";

export function* onLoad(action: PayloadAction<string>): SagaIterator<void> {
  yield put(setError(false));
  yield put(setEmailExpired(false));

  const activationCode = action.payload;
  const apiHandler: APIHandler = yield call(() => factory());
  try {
    const challengeQuestion: ChallengeQuestionResponseType = yield call(() =>
      apiHandler.getChallengeQuestion(activationCode)
    );
    yield put(afterChallengeQuestionFetch(challengeQuestion.question));
  } catch (e: any) {
    const errorMessage = JSON.parse(e.body).errors[0].message as string;

    if (
      e.statusCode === 404 &&
      errorMessage.includes(ErrorMessageEnum.ERROR_USER_DOES_NOT_EXIST)
    ) {
      yield put(setInvalidCode(true));
    } else if (
      e.statusCode === 400 &&
      (errorMessage === ErrorMessageEnum.ERROR_EMAIL_EXPIRED ||
        errorMessage === ErrorMessageEnum.ERROR_REGISTRATION_LINK_EXPIRED)
    ) {
      yield put(setEmailExpired(true));
    } else {
      yield put(setError(true));
    }
    yield put(afterChallengeQuestionFetch(null));
  }
}

export function* onSubmission(
  action: PayloadAction<ChallengeQuestionSubmissionPayloadType>
): SagaIterator<void> {
  yield put(setEmailError(false));
  yield put(setInvalidCode(false));
  const { email, answer } = action.payload;
  const activationCode = yield select(selectActivationCode);
  const apiHandler: APIHandler = yield call(() => factory());
  try {
    const challengeQuestionSubmission: ChallengeQuestionSubmissionResponsePayload =
      yield call(() =>
        apiHandler.submitChallengeAnswer(activationCode, { answer, email })
      );
    if (challengeQuestionSubmission?.access_token) {
      storage.setItem("SESSION_KEY", challengeQuestionSubmission.access_token);
      apiHandler.sessionKey = challengeQuestionSubmission.access_token;
    }
    yield put(
      afterChallengeQuestionSubmissionResponse(challengeQuestionSubmission)
    );
  } catch (e: any) {
    yield put(setEmailError(false));
    yield put(setInvalidCode(false));
    const errorMessage = JSON.parse(e.body).errors[0].message as string;

    if (
      e.statusCode === 401 &&
      errorMessage === ErrorMessageEnum.ERROR_INVALID_ANSWER
    ) {
      yield put(setRetries());
    } else if (
      e.statusCode === 400 &&
      errorMessage.includes(ErrorMessageEnum.ERROR_EMAIL_MISMATCH)
    ) {
      yield put(setRetries());
    } else if (
      e.statusCode === 400 &&
      (errorMessage === ErrorMessageEnum.ERROR_EMAIL_EXPIRED ||
        errorMessage === ErrorMessageEnum.ERROR_REGISTRATION_LINK_EXPIRED)
    ) {
      yield put(setEmailExpired(true));
    } else if (
      e.statusCode === 404 &&
      errorMessage.includes(ErrorMessageEnum.ERROR_USER_DOES_NOT_EXIST)
    ) {
      yield put(setInvalidCode(true));
    }
    yield put(
      afterChallengeQuestionSubmissionResponse({
        access_token: "",
        token_type: "",
        expires_in: "",
      })
    );
  }
}

// watcher function
export default function* challengeQuestionSaga() {
  yield takeLatest(loadChallengeQuestion.type, onLoad);
  yield takeLatest(onAnswerSubmission.type, onSubmission);
}
